/**
 * Tolerance in pixels for the "scrolled to the end" comparison. Sub-pixel layout rounding and
 * fractional device pixel ratios make an exact `scrollTop === scrollHeight - clientHeight` match
 * unreliable, so the last few pixels count as the end.
 */
export const SCROLL_END_TOLERANCE_PX = 10;

/** The scroll geometry of the panel a legal document is rendered in. */
export interface ScrollMetrics {
    scrollHeight: number;
    clientHeight: number;
    scrollTop: number;
}

/**
 * Whether the reader has reached the end of the scrollable content.
 *
 * Content that fits the panel satisfies this by construction (`scrollHeight === clientHeight` and
 * `scrollTop === 0`), which is what allows a short document to be released without any scrolling.
 */
function isScrolledToEnd(metrics: ScrollMetrics): boolean {
    return Math.abs(metrics.scrollHeight - metrics.clientHeight - metrics.scrollTop) < SCROLL_END_TOLERANCE_PX;
}

/**
 * Decides whether the acceptance checkbox of an agreement step may be enabled.
 *
 * This is the single place that grants consent capability, so all three guards live together:
 *
 * - `isAlreadyAccepted` — the checkbox is already ticked (restored wizard state, or the user just
 *   checked it): consent was granted before and is not revoked by a later re-measurement.
 * - `isContentLoaded` — no consent for content that is not on screen. Without this guard an EMPTY
 *   container would qualify: the browser clamps `scrollTop` to 0 while `scrollHeight` collapses to
 *   `clientHeight`, so the geometry of an empty panel is indistinguishable from a fully read short
 *   document. This is why the load state — not just the geometry — is part of the decision.
 * - geometry — either the document fits the panel entirely (nothing left to read) or it has been
 *   scrolled to its end. For content longer than the panel, scrolling to the end stays mandatory.
 *
 * @param metrics - Scroll geometry of the panel, measured AFTER the content was inserted into the DOM
 * @param isContentLoaded - Whether the panel currently holds successfully loaded document content
 * @param isAlreadyAccepted - Whether the acceptance checkbox is already ticked
 * @returns `true` when the acceptance checkbox may be enabled
 */
export function canAcceptAgreement(
    metrics: ScrollMetrics,
    isContentLoaded: boolean,
    isAlreadyAccepted: boolean
): boolean {
    if (isAlreadyAccepted) {
        return true;
    }
    if (!isContentLoaded) {
        return false;
    }
    return isScrolledToEnd(metrics);
}
